import React, { Fragment, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
// import { LinkContainer } from 'react-router-bootstrap';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';

import ActivityTableRow from './ActivityTableRow';
import ActivityMobileItem from './ActivityMobileItem';
import CaseModal from '../modals/case';
import CaseRequestDateModal from '../modals/case/CaseRequestDateModal';
import CaseRequestDateHospitalModal from '../modals/case/CaseRequestDateHospitalModal';
import CaseApproveDateModal from '../modals/case/CaseApproveDateModal';
import CaseRequestConsultationDateModal from '../modals/case/CaseRequestConsultationDateModal';
import CaseApproveConsultationDateModal from '../modals/case/CaseApproveConsultationDateModal';
import CaseDateModal from '../modals/case/CaseDateModal';
import Alert from '../shared/Alert';

import { toggleSidebar } from '../../actions/sidebarActions';
import {
  setActiveCaseById,
  discardActiveCase,
  sendChangeDateHospitalEmail,
  requestCaseDateUpdate,
  approveRequestDateUpdate,
  rejectRequestDateUpdate,
  requestConsultationDateUpdate,
  approveRequestConsultationDateUpdate,
  rejectRequestConsultationDateUpdate,
} from '../../actions/casesActions';
import { deleteActivity } from '../../actions/activityActions';

import { userRoles } from '../../constants';

import './activity.scss';

const getActivityTimestamp = (value) => {
  if (!value) {
    return 0;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate().getTime();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'number') {
    return value;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const ActivityTable = (props) => {
  const {
    activityList,
    toggleSidebar,
    dashboardActivities,
    setActiveCaseById,
    discardActiveCase,
    deleteActivity,
    users = [],
    onlyRecent,
    role,
    hospitals,
    surgeons,
    procedures,
    updateCaseDoc,
    sendChangeDateHospitalEmail,
    rejectRequestDateUpdate,
    requestCaseDateUpdate,
    approveRequestDateUpdate,
    requestConsultationDateUpdate,
    approveRequestConsultationDateUpdate,
    rejectRequestConsultationDateUpdate,
  } = props;

  const [caseModalOpen, toggleModal] = useState(false);
  const [alert, toggleAlert] = useState(false);

  const [selectedCase, setSelectedCase] = useState(null);
  const [dateModalOpen, toggleDateModal] = useState(false);
  const [dateHospitalModalOpen, toggleDateHospitalModal] = useState(false);
  const [requestDateModalOpen, toggleRequestDateModal] = useState(false);
  const [approveDateModalOpen, toggleApproveDateModal] = useState(false);
  const [requestConsultationDateModalOpen, toggleRequestConsultationDateModal] = useState(false);
  const [approveConsultationDateModalOpen, toggleApproveConsultationDateModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCaseOpen = (caseId) => {
    setActiveCaseById(caseId);
    toggleModal(true);
  };

  const handleModalClose = () => {
    toggleModal(false);
    discardActiveCase();
  };

  const handleDelete = (activityId) => {
    deleteActivity(activityId).then(() => toggleAlert(true));
  };

  // Request date functionality

  const onSubmitDate = async (date, hospitalId) => {
    try {
      setLoading(true);
      const hospital = hospitals?.find((h) => h.id === hospitalId);
      const doc = {
        date: date?.format(),
        hospital: hospital?.name || '',
        hospitalUniq: { id: hospitalId, name: hospital?.name || '' }
      };
      await updateCaseDoc(selectedCase.id, doc);
      sendChangeDateHospitalEmail(selectedCase.id, { ...selectedCase, ...doc }, selectedCase);
      toggleDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitRequestDate = async (date) => {
    try {
      setLoading(true);
      await requestCaseDateUpdate(selectedCase, date);
      toggleRequestDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitRequestDateHospital = async (date, hospital) => {
    try {
      setLoading(true);
      await requestCaseDateUpdate(selectedCase, date, hospital);
      toggleDateHospitalModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onApproveDate = async () => {
    try {
      if (selectedCase?.requestedSurgeryDate?.newDate || selectedCase?.requestedSurgeryDateNew) {
        setLoading(true);
        await approveRequestDateUpdate(selectedCase);
        toggleApproveDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onRejectDate = async () => {
    try {
      if (selectedCase?.requestedSurgeryDate?.newDate || selectedCase?.requestedSurgeryDateNew) {
        setLoading(true);
        await rejectRequestDateUpdate(selectedCase);
        toggleApproveDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onAdminApproveDateClick = (row) => {
    handleModalClose();
    setSelectedCase(row);
    toggleApproveDateModal(true);
  };

  const onTBCClick = (row) => {
    handleModalClose();
    const canDirectlyChangeCaseDate = role === userRoles.GLOBAL_ADMIN.name;
    setSelectedCase(row);
    if (canDirectlyChangeCaseDate) {
      toggleDateModal(true);
    } else {
      toggleDateHospitalModal(true);
    }
  };

  const onRequestDateClick = (row) => {
    handleModalClose();
    setSelectedCase(row);
    toggleRequestDateModal(true);
  };

  const onSubmitRequestConsultationDate = async (date) => {
    try {
      setLoading(true);
      await requestConsultationDateUpdate(selectedCase, date);
      toggleRequestConsultationDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onApproveConsultationDate = async () => {
    try {
      if (selectedCase?.requestedConsultationDate?.newDate || selectedCase?.requestedConsultationDateNew) {
        setLoading(true);
        await approveRequestConsultationDateUpdate(selectedCase);
        toggleApproveConsultationDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onRejectConsultationDate = async () => {
    try {
      if (selectedCase?.requestedConsultationDate?.newDate || selectedCase?.requestedConsultationDateNew) {
        setLoading(true);
        await rejectRequestConsultationDateUpdate(selectedCase);
        toggleApproveConsultationDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onAdminApproveConsultationDateClick = (row) => {
    handleModalClose();
    setSelectedCase(row);
    toggleApproveConsultationDateModal(true);
  };

  const onRequestConsultationDateClick = (row) => {
    handleModalClose();
    setSelectedCase(row);
    toggleRequestConsultationDateModal(true);
  };

  const list = useMemo(() => {
    const sortedActivity = [...(activityList || [])].sort(
      (a, b) => getActivityTimestamp(b.date) - getActivityTimestamp(a.date)
    );

    return onlyRecent ? sortedActivity.slice(0, 5) : sortedActivity;
  }, [activityList, onlyRecent]);

  const hospitalList = useMemo(() => {
    if (selectedCase && selectedCase.surgeonId) {
      const surgeon = surgeons?.find((s) => s.id === selectedCase.surgeonId);
      return surgeon ? hospitals?.filter((h) => surgeon?.hospitals?.includes(h.id)) : []
    }
    return [];
  }, [hospitals, surgeons, selectedCase]);

  return (
    <Box className="activity-table-container">
      <div className="table-title-container">
        <div className="d-flex">
          {!dashboardActivities && (
            <Box sx={{ display: { sm: 'none' } }}>
              <div className="m-r-sm">
                <IconButton edge="start" color="primary" aria-label="menu" onClick={toggleSidebar}>
                  <MenuIcon />
                </IconButton>
              </div>
            </Box>
          )}
          <div className="table-title">
            {dashboardActivities ? 'Recent Activity' : 'Activity'}
            <div className="badge">{activityList.length}</div>
          </div>
        </div>
        {/*{dashboardActivities && (*/}
        {/*  <LinkContainer to="/activity">*/}
        {/*    <div className="table-action">See All</div>*/}
        {/*  </LinkContainer>*/}
        {/*)}*/}
      </div>
      {activityList.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <table className="activity-table">
                <tbody>
                {list.map((activity) => (
                  <ActivityTableRow
                    key={activity.id}
                    activity={activity}
                    onCaseOpen={handleCaseOpen}
                    onDelete={handleDelete}
                    users={users}
                  />
                ))}
                </tbody>
              </table>
            </Box>
            <Box sx={{ display: { md: 'none' } }}>
              <div className="p-md">
                {list.map((activity) => (
                  <ActivityMobileItem
                    key={activity.id}
                    activity={activity}
                    onCaseOpen={handleCaseOpen}
                    onDelete={handleDelete}
                    users={users}
                  />
                ))}
              </div>
            </Box>
          </Fragment>
      ) : (
        <div className="empty-block">
          <div>No Activity</div>
          <div className="additional-text">{dashboardActivities && '(last 24 hours)'}</div>
        </div>
      )}
      <CaseModal
        open={caseModalOpen}
        onClose={handleModalClose}
        onAdminApproveDateClick={onAdminApproveDateClick}
        onTBCClick={onTBCClick}
        onRequestDateClick={onRequestDateClick}
        onAdminApproveConsultationDateClick={onAdminApproveConsultationDateClick}
        onRequestConsultationDateClick={onRequestConsultationDateClick}
      />
      <Alert
        open={alert}
        variant="success"
        onClose={() => toggleAlert(false)}
        message="Activity has been successfully deleted"
      />

      <CaseRequestDateModal
        open={requestDateModalOpen}
        onClose={() => {
          toggleRequestDateModal(false);
          setSelectedCase(null);
        }}
        selectedCase={selectedCase}
        onSubmit={onSubmitRequestDate}
        loading={loading}
      />

      <CaseRequestDateHospitalModal
        open={dateHospitalModalOpen}
        onClose={() => {
          toggleDateHospitalModal(false);
          setSelectedCase(null);
        }}
        selectedCase={selectedCase}
        onSubmit={onSubmitRequestDateHospital}
        loading={loading}
        hospitals={hospitals}
        surgeons={surgeons}
      />

      <CaseRequestConsultationDateModal
        open={requestConsultationDateModalOpen}
        onClose={() => {
          toggleRequestConsultationDateModal(false);
          setSelectedCase(null);
        }}
        selectedCase={selectedCase}
        onSubmit={onSubmitRequestConsultationDate}
        loading={loading}
      />

      <CaseApproveDateModal
        open={approveDateModalOpen}
        onClose={() => {
          toggleApproveDateModal(false);
          setSelectedCase(null);
        }}
        selectedCase={selectedCase}
        onApprove={onApproveDate}
        onReject={onRejectDate}
        loading={loading}
        hospitals={hospitals}
      />

      <CaseApproveConsultationDateModal
        open={approveConsultationDateModalOpen}
        onClose={() => {
          toggleApproveConsultationDateModal(false);
          setSelectedCase(null);
        }}
        selectedCase={selectedCase}
        onApprove={onApproveConsultationDate}
        onReject={onRejectConsultationDate}
        loading={loading}
      />

      <CaseDateModal
        open={dateModalOpen}
        onClose={() => {
          toggleDateModal(false);
          setSelectedCase(null);
        }}
        selectedCase={selectedCase}
        onSubmit={onSubmitDate}
        hospitalList={hospitalList}
        procedures={procedures}
        loading={loading}
        isAdmin={role === userRoles.GLOBAL_ADMIN.name}
      />
    </Box>
  );
};

ActivityTable.propTypes = {
  activityList: PropTypes.array,
  dashboardActivities: PropTypes.bool,
  toggleSidebar: PropTypes.func,
  setActiveCaseById: PropTypes.func,
  discardActiveCase: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    role: state.user.currentUser.role,
    hospitals: state?.hospitals?.list || [],
    surgeons: state?.surgeons?.list || [],
    procedures: state?.procedures?.list || [],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    toggleSidebar: () => dispatch(toggleSidebar()),
    setActiveCaseById: (caseId) => dispatch(setActiveCaseById(caseId)),
    discardActiveCase: () => dispatch(discardActiveCase()),
    deleteActivity: (id) => dispatch(deleteActivity(id)),
    sendChangeDateHospitalEmail: (id, data, previousData) => dispatch(sendChangeDateHospitalEmail(id, data, previousData)),
    requestCaseDateUpdate: (activeCase, date, hospital) => dispatch(requestCaseDateUpdate(activeCase, date, hospital)),
    approveRequestDateUpdate: (activeCase) => dispatch(approveRequestDateUpdate(activeCase)),
    rejectRequestDateUpdate: (activeCase) => dispatch(rejectRequestDateUpdate(activeCase)),
    requestConsultationDateUpdate: (activeCase, date) => dispatch(requestConsultationDateUpdate(activeCase, date)),
    approveRequestConsultationDateUpdate: (activeCase) => dispatch(approveRequestConsultationDateUpdate(activeCase)),
    rejectRequestConsultationDateUpdate: (activeCase) => dispatch(rejectRequestConsultationDateUpdate(activeCase)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityTable);
